import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import useSEO from "@/lib/useSEO";

const STATUS_LABELS = {
  pending: "In attesa di conferma dello Studio",
  confirmed: "Confermata",
  cancelled: "Annullata",
};

export default function BookingManage() {
  useSEO({ title: "Gestisci appuntamento", description: "Visualizza o annulla il tuo appuntamento con Giovanna Matino Interior." });
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/bookings/manage/${token}`).then((r) => setInfo(r.data)).catch(() => setNotFound(true));
  }, [token]);

  const cancel = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/manage/${token}/cancel`);
      setInfo((p) => ({ ...p, ...data, can_cancel: false }));
      toast.success("Appuntamento annullato");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Annullamento non riuscito");
    } finally { setBusy(false); setConfirming(false); }
  };

  if (notFound) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-bone px-6" data-testid="booking-manage-notfound">
        <div className="max-w-xl w-full bg-white border border-hair p-10 text-center">
          <X size={26} className="mx-auto text-taupe" />
          <h1 className="font-serif text-3xl mt-4">Collegamento non valido</h1>
          <Link to="/prenota" className="btn-ink mt-8 inline-flex" data-testid="manage-rebook">Prenota una consulenza</Link>
        </div>
      </div>
    );
  }
  if (!info) {
    return <div className="min-h-[70vh] flex items-center justify-center bg-bone"><Loader2 size={32} className="animate-spin text-gold" /></div>;
  }

  return (
    <div className="min-h-[70vh] bg-bone px-6 py-16 flex items-start justify-center" data-testid="page-booking-manage">
      <div className="max-w-xl w-full bg-white border border-hair p-8 md:p-12">
        <span className="eyebrow">Il tuo appuntamento</span>
        <h1 className="font-serif text-3xl md:text-4xl mt-3">Gestisci appuntamento</h1>
        <div className="mt-4 text-sm" data-testid="manage-status">
          <span className={`inline-block px-3 py-1 text-[11px] tracking-[0.14em] uppercase border ${info.status === "confirmed" ? "border-emerald-700 text-emerald-700" : info.status === "cancelled" ? "border-red-700 text-red-700" : "border-gold text-gold"}`}>
            {STATUS_LABELS[info.status] || info.status}
          </span>
        </div>
        <div className="mt-6 border border-hair divide-y divide-hair text-sm">
          <div className="flex justify-between p-4"><span className="text-taupe">Servizio</span><span className="font-medium">{info.consultation_type}</span></div>
          <div className="flex justify-between p-4"><span className="text-taupe">Data</span><span>{info.date_label}</span></div>
          <div className="flex justify-between p-4"><span className="text-taupe">Ora</span><span>{info.time_slot} · {info.duration_label}</span></div>
          {info.location_info && <div className="flex justify-between p-4"><span className="text-taupe">Modalità o luogo</span><span className="text-right">{info.location_info}</span></div>}
        </div>

        {info.can_cancel && !confirming && (
          <button onClick={() => setConfirming(true)} className="mt-8 w-full min-h-[48px] border border-red-700 text-red-700 text-xs tracking-[0.14em] uppercase hover:bg-red-700 hover:text-bone transition-colors" data-testid="manage-cancel-open">
            Annulla appuntamento
          </button>
        )}
        {info.can_cancel && confirming && (
          <div className="mt-8 border border-red-200 bg-red-50 p-5" data-testid="manage-cancel-confirm">
            <p className="text-sm text-red-900">Vuoi annullare questo appuntamento? Lo slot tornerà disponibile per altri clienti.</p>
            <div className="flex gap-3 mt-4">
              <button onClick={cancel} disabled={busy} className="flex-1 min-h-[44px] bg-red-700 text-bone text-xs tracking-[0.14em] uppercase disabled:opacity-60" data-testid="manage-cancel-yes">
                {busy ? "Annullamento…" : "Sì, annulla"}
              </button>
              <button onClick={() => setConfirming(false)} className="flex-1 min-h-[44px] border border-hair text-xs tracking-[0.14em] uppercase hover:border-ink" data-testid="manage-cancel-no">
                Mantieni
              </button>
            </div>
          </div>
        )}
        {info.status === "cancelled" && (
          <p className="mt-8 text-sm text-taupe flex items-center gap-2" data-testid="manage-cancelled-note">
            <CheckCircle2 size={16} className="text-taupe" /> Questo appuntamento è stato annullato.
          </p>
        )}
        <p className="text-xs text-taupe mt-6 leading-relaxed">
          Per modificare data o orario, contattaci: rispondi all'e-mail di conferma o usa i contatti dello Studio.
        </p>
        <Link to="/" className="text-xs tracking-[0.14em] uppercase text-taupe hover:text-ink mt-6 inline-block" data-testid="manage-home">← Torna alla home</Link>
      </div>
    </div>
  );
}
